import { tz } from '@date-fns/tz';
import { format } from 'date-fns';
import { styled } from 'styled-components';

import { Box } from 'components/Box/Box';
import { DefaultButton as TooltipButton } from 'components/LegacyTooltip/LegacyTooltip.styled';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { colors } from 'theme/theme';
import { DATE_FNS_LOCALES, getDate, humanDate, shorterTime } from 'utils/date';
import { getUserTimezone } from 'utils/timezones';

const StyledTooltipButton = styled(TooltipButton)`
  color: ${colors.lightContentGrey};

  &:hover {
    color: ${colors.brandBlue};
  }
`;

type TimeTooltipProps = {
  startDateTime: string;
  endDateTime?: string;
  timezone: string | null | undefined;
};

export function TimeTooltip({
  startDateTime,
  endDateTime,
  timezone,
}: TimeTooltipProps) {
  const startTime = shorterTime(startDateTime, timezone, !endDateTime);
  const endTime = endDateTime ? shorterTime(endDateTime, timezone, true) : '';
  return (
    <Box ml="8px" mr="4px">
      <Tooltip
        variant="filled"
        content={
          <>
            {startTime}
            {endTime ? ' - ' : ''}
            {endTime}
          </>
        }
      >
        <StyledTooltipButton />
      </Tooltip>
    </Box>
  );
}

type SingleDayDateTimeProps = {
  startsLocal: string;
  endsLocal: string;
  startTime: string | null | undefined;
  endTime: string | null | undefined;
  timezone?: string | null;
  'data-qa-id'?: string;
};

export function SingleDayDateTime({
  startsLocal,
  endsLocal,
  startTime,
  endTime,
  timezone,
  'data-qa-id': dataQaId,
}: SingleDayDateTimeProps) {
  const startDateTime = shorterTime(startsLocal, timezone, !endsLocal);
  const endDateTime = endsLocal ? shorterTime(endsLocal, timezone, true) : '';
  const userTimezone = getUserTimezone();
  return (
    <div
      data-qa-id={dataQaId}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      <div>{humanDate(startsLocal, timezone)}</div>
      {startTime && endTime && (
        <Box display="flex" alignItems="center">
          <>
            {startDateTime}
            {endTime ? ' - ' : ''}
            {endDateTime}
          </>
          {userTimezone !== timezone && (
            <TimeTooltip
              startDateTime={startsLocal}
              endDateTime={endsLocal}
              timezone={userTimezone}
            />
          )}
        </Box>
      )}
    </div>
  );
}

type DateTimeProps = {
  datetime: string;
  timezone: string | null | undefined;
  'data-qa-id'?: string;
};

export function DateTime({
  datetime,
  timezone,
  'data-qa-id': dataQaId,
}: DateTimeProps) {
  const userTimezone = getUserTimezone();

  return (
    <div data-qa-id={dataQaId}>
      <div style={{ display: 'inline-block' }}>
        {timezone
          ? format(datetime, 'MMMM d, yyyy, h:mm a zzz', {
              in: tz(timezone),
              locale: DATE_FNS_LOCALES[CURRENT_LOCALE()],
            })
          : format(getDate(datetime), 'MMMM d, yyyy, h:mm a', {
              locale: DATE_FNS_LOCALES[CURRENT_LOCALE()],
            })}
      </div>

      {userTimezone !== timezone && (
        <div style={{ display: 'inline-block', marginLeft: '4px' }}>
          <TimeTooltip startDateTime={datetime} timezone={userTimezone} />
        </div>
      )}
    </div>
  );
}
